/**
 * Global feature flags for UI surfaces that aren't ready for users.
 *
 * Flip to `true` when the feature is ready. Keep as `false` to hide the UI
 * everywhere at once (no stray banners, settings tabs, or buttons).
 */

// DialIQ / Smart Redial — ENABLED. All gated entry points are now live:
// LeadsHeader button, Leads page panel, DialerSettings section,
// UnreachableBadge, RedialThresholdToast.
export const DIAL_IQ_ENABLED = true;

// AI Calling & InsuraTraining — env-driven so the SAME code stays on every
// branch (no per-branch toggling → no merge conflicts / accidental unlocks).
//   - UNSET or "false"  → feature is locked behind the "Coming Soon" UI.
//   - "true"            → feature is live (nav navigates, the real page renders).
// Set in `.env.local` on dev (= true to test), and on Render for prod when ready.
// NEXT_PUBLIC_* vars are inlined at build, so restart the dev server after a change.
// NOTE: AI Calling still applies its own Super Agent plan gate on the page even
// when this flag is on — the flag only removes the "Coming Soon" lock.
export const AI_CALLING_ENABLED =
  process.env.NEXT_PUBLIC_AI_CALLING_ENABLED === "true";
export const MARKETING_ENABLED =
  process.env.NEXT_PUBLIC_MARKETING_ENABLED === "true";

/** Per-user Marketing access — global flag, OR approved off `marketingWaitlist`, OR `marketingEnabled`. */
export function marketingUnlockedFor(user: unknown): boolean {
  if (MARKETING_ENABLED) return true;
  const prefs = (user as { appPreferences?: Record<string, unknown> } | null | undefined)?.appPreferences;
  if (!prefs) return false;
  const waitlist = prefs.marketingWaitlist as { status?: string } | undefined;
  return waitlist?.status === "approved" || prefs.marketingEnabled === true;
}
export const INSURATRAINING_ENABLED =
  process.env.NEXT_PUBLIC_INSURATRAINING_ENABLED === "true";

// DevTools protection — DISABLED by default (2026-07-28).
//
// What it did: watched `outerWidth - innerWidth` for a 250px jump and, on 3
// consecutive hits, replaced window.fetch / XMLHttpRequest.open / WebSocket /
// EventSource with throwing stubs, cleared localStorage and forced a sign-out.
//
// Why it's off:
//   - It took agents OFFLINE MID-CALL. A hidden/minimized window reports
//     innerWidth 0, which looks identical to a ~1900px docked panel. Michael
//     Gleichner's PostHog replay: "Window became hidden" → 20s later a stream of
//     `Error: Network blocked` thrown from our own devtools-protection.ts, the
//     lead page showing "Error loading lead details", repeated forced logouts,
//     ending in a Rageclick. Browser zoom at 125% trips it too (~288px swing).
//   - It protects nothing real. There are no server secrets in the bundle (all
//     client config is NEXT_PUBLIC_*), and the API authorises independently —
//     137 controllers behind 15 guards with server-side agency scoping. Console
//     access lets an agent read their own JWT and call the API as themselves,
//     both of which they can already do.
//   - It stops no actual attacker: proxy the traffic, disable JS, open devtools
//     before load, or just use curl. It only ever inconvenienced honest users.
//
// The real controls are server-side authorisation plus the `audits` /
// `security_events` tables, which record what someone DID, not what tool they
// had open.
//
// Set to "true" to re-enable. The hidden-window false positive is fixed either
// way, so re-enabling won't bring back the tab-switch bug.
export const DEVTOOLS_PROTECTION_ENABLED =
  process.env.NEXT_PUBLIC_DEVTOOLS_PROTECTION_ENABLED === "true";

// Navbar wallet pill (dial-credit balance + its breakdown popover). Hidden by
// default: the balance only funds AI Calling and DNC Scrub, and showing a bare
// "0 min" in the header made agents think they couldn't dial at all. The
// breakdown panel exists to explain that, but the whole surface stays off until
// we choose to show it.
//   - UNSET or "false" → the pill does not render anywhere.
//   - "true"           → pill + breakdown popover render.
// Gates BOTH entry points: the navbar pill and the avatar-dropdown row, so the
// balance can't leak back in through the menu while the pill is hidden.
// NEXT_PUBLIC_* is inlined at build → restart the dev server after changing it.
export const DIAL_CREDITS_UI_ENABLED =
  process.env.NEXT_PUBLIC_DIAL_CREDITS_UI_ENABLED === "true";

// Lead Sniper — the native exchange tab. Our own UI; the pricing/stock/order
// engine is the LeadStoreSniper backend (bridged per-agent via a signed session
// token, no second login). Env-driven so the same code stays locked behind
// "Coming Soon" until we flip it live per environment.
//   - UNSET or "false" → "Coming Soon" lock (waitlist).
//   - "true"           → the real /lead-sniper page renders.
// The page also needs NEXT_PUBLIC_LEAD_SNIPER_ORIGIN (the exchange origin) and,
// on the IC backend, EMBED_SECRET + LEAD_SNIPER_ORIGIN.
export const LEAD_SNIPER_ENABLED =
  process.env.NEXT_PUBLIC_LEAD_SNIPER_ENABLED === "true";

// Community Forum — HIDDEN EVERYWHERE by default (2026-07-29).
//
// The per-account gating in AppSidebar (hide for enterprise / enterprise
// hierarchy / white-label / agencies with the Community toggle off) has a hole:
// SOLO users with no agency fall through every branch, because the agency-flag
// check is guarded by `!!currentAgencyId`. Independents were therefore seeing a
// Forum tab that 403s the moment they open it — which is worse than no tab.
//
// Rather than bolt another condition onto that chain, this flag short-circuits
// the whole thing: unset/"false" removes the nav entry for EVERY account type,
// including system admins, and stops the 60s "new posts" poll. Flip to "true"
// and the original per-account rules take over again unchanged.
//
// The /forum route itself is separately guarded server-side — this only controls
// whether the entry point is advertised.
// NEXT_PUBLIC_* is inlined at build → restart the dev server after changing it.
export const FORUM_ENABLED = process.env.NEXT_PUBLIC_FORUM_ENABLED === "true";

// Community photo uploads — OFF (2026-09-02). A screenshot of a CRM screen,
// an application or an ID is the one client-PII leak no text filter can read,
// so the composer hides the control and the backend refuses uploads unless
// FORUM_IMAGE_UPLOADS_ENABLED=true is set there too.
export const FORUM_IMAGE_UPLOADS_ENABLED = process.env.NEXT_PUBLIC_FORUM_IMAGE_UPLOADS === "true";

// Agent-created rooms — LOCKED (2026-09-02). Rooms are curated by InsuraCentral
// for now; the backend refuses non-admin creation unless FORUM_USER_ROOMS_ENABLED
// is set there too. Admins keep the "Start a community" control.
export const FORUM_USER_ROOMS_ENABLED = process.env.NEXT_PUBLIC_FORUM_USER_ROOMS === "true";

/**
 * Per-user Lead Sniper access — for a staged rollout / beta before flipping the
 * global flag on for everyone. Returns true when EITHER the global flag is on,
 * OR this specific user was let in: approved off the waitlist
 * (`app_preferences.leadSniperWaitlist.status === "approved"`) or given an
 * explicit `app_preferences.leadSniperEnabled === true` override. That lets us
 * enable individual users on an environment with a single DB change — no code
 * deploy, no unlocking it for the whole environment.
 *
 * Pass the inner user object (the one carrying `appPreferences`, e.g.
 * `user.user` from useUserContext). System admins are handled separately by the
 * gate and don't need this.
 */
export function leadSniperUnlockedFor(user: unknown): boolean {
  if (LEAD_SNIPER_ENABLED) return true;
  // LeadStoreSniper's free dialer account buys its leads and its dialer there.
  if ((user as { accountType?: string } | null | undefined)?.accountType === "dialer") return true;
  const prefs = (user as { appPreferences?: Record<string, unknown> } | null | undefined)
    ?.appPreferences;
  if (!prefs) return false;
  const waitlist = prefs.leadSniperWaitlist as { status?: string } | undefined;
  return waitlist?.status === "approved" || prefs.leadSniperEnabled === true;
}

/**
 * Per-user AI Calling access — global flag, OR approved off the
 * `aiCallingWaitlist`, OR an explicit `aiCallingEnabled` override.
 *
 * ⚠️ This answers "is AI Calling LIVE for this person", nothing more. The Super
 * Agent plan gate is a separate check and still applies on top of it — being let
 * off the waitlist does not buy the plan.
 */
export function aiCallingUnlockedFor(user: unknown): boolean {
  if (AI_CALLING_ENABLED) return true;
  const prefs = (user as { appPreferences?: Record<string, unknown> } | null | undefined)
    ?.appPreferences;
  if (!prefs) return false;
  const waitlist = prefs.aiCallingWaitlist as { status?: string } | undefined;
  return waitlist?.status === "approved" || prefs.aiCallingEnabled === true;
}

/**
 * Per-user InsuraMentor access — same staged-rollout idea as
 * `leadSniperUnlockedFor`: global flag, OR this specific user approved off the
 * `insuraTrainingWaitlist`, OR an explicit `insuraTrainingEnabled` override.
 * Lets a single prod DB change open the roleplay for one agent without
 * unlocking it for the whole environment.
 *
 * ⚠️ Mirror any change here in the other three gates: AppSidebar,
 * MobileBottomNav and DialCreditsIndicator. Four places decide whether
 * InsuraMentor looks live, and a half-flipped feature is worse than a locked one.
 */
export function insuraMentorUnlockedFor(user: unknown): boolean {
  if (INSURATRAINING_ENABLED) return true;
  const prefs = (user as { appPreferences?: Record<string, unknown> } | null | undefined)
    ?.appPreferences;
  if (!prefs) return false;
  const waitlist = prefs.insuraTrainingWaitlist as { status?: string } | undefined;
  return waitlist?.status === "approved" || prefs.insuraTrainingEnabled === true;
}

// DialIQ Predictive Dial — globally on/off via backend env var
// PREDICTIVE_DIAL_ENABLED=true. Frontend doesn't gate at all; it just calls
// the backend's startSession when source=dialiq. If the backend says disabled,
// the frontend silently falls back to standard PowerDialer. Per-session
// abandonment auto-throttle (2%/2.5% thresholds) is the TCPA safety net.
